import { type ApolloClient, type NormalizedCacheObject, gql } from '@apollo/client/core';
import type { Query } from '../../generated/graphql';

/**
 * The settlement's **printable sheet URL**, on the legacy connection.
 *
 * The read and the tax, events and deals sections have moved to AppSchema and live in
 * `api/App/Settlement`. This field has not, and it is not the same field as the one that looks
 * like it. The legacy settlement type publishes two:
 *
 * - `printableUrl` — a route helper (`…event_artist_settlement_settlement_sheet_url`), which always
 *   resolves. This is what the modal's "Settlement Sheet" link opens.
 * - `pdfDownloadUrl` — `object.attachment&.url`, which is nil until `generateSettlementPdf` has
 *   run, and nothing in `@vp/admin` calls that mutation.
 *
 * AppSchema's `Settlement` republishes only the second. Treating it as the first one's new name
 * would hide the link on effectively every settlement, so the first is read from here instead —
 * the same split, for the same reason, that `api/Offer/` carries for the offer sheet's
 * `htmlContent`.
 *
 * It lives in the legacy namespace rather than beside the rest of the settlement in `App/` because
 * `App/` is defined by its client: `api-layer.md`'s table reads `App/` => `gqlApiClient`, and a
 * legacy `apiClient` call inside it would misrepresent which schema that directory speaks to.
 *
 * **Delete this** once `printable_url` is published on `App::Settlement::SettlementType`: the field
 * folds into `SETTLEMENT_FIELDS`, and `@vp/admin`'s `composables/useSettlementPrintableUrl.ts` —
 * which resolves the legacy client on the modal's behalf and does not compile without this — goes
 * with it.
 */
export const SETTLEMENT_PRINTABLE_URL_QUERY = gql`
  query settlementPrintableUrl($settlementId: Int!) {
    eventArtistSettlement(id: $settlementId) {
      printableUrl
    }
  }
`;

export const fetchSettlementPrintableUrl = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<string | undefined> => {
  const { data } = await client.query<Query>({
    query: SETTLEMENT_PRINTABLE_URL_QUERY,
    variables: { settlementId: id },
    fetchPolicy: 'no-cache',
  });

  return data.eventArtistSettlement?.printableUrl ?? undefined;
};
